import clsx from "clsx";

import type { MetricKey } from "../types";

import { Button as ButtonV2 } from "../../Button";
import { CheckIcon, GearIcon } from "../../Icons";
import { PopoverMenu } from "../../PopoverMenu";
import { Typo } from "../../Typo";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";
import { usePerformanceReportAnalyticsContext } from "../providers/PerformanceReportAnalyticsProvider";

type ColumnsPopoverProps = {
  hidden: Partial<Record<MetricKey, boolean>>;
  onToggle: (key: MetricKey) => void;
};

const Row = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-start transition-colors hover:bg-overlay-opacity-state-hover"
  >
    {children}
  </button>
);

/** Column show/hide configurator. Reg %, App %, Fnd % are hidden by default. */
export const ColumnsPopover = ({ hidden, onToggle }: ColumnsPopoverProps) => {
  const translations = usePerformanceReportTranslations();
  const analytics = usePerformanceReportAnalyticsContext();
  const funnelCols = translations.metricColumns.filter((c) => c.type !== "pct");
  const conversionCols = translations.metricColumns.filter(
    (c) => c.type === "pct",
  );

  return (
    <PopoverMenu
      asChild
      place="bottom"
      contentClassName="scrollbar-minimal max-h-[360px] min-w-[232px] overflow-y-auto rounded-xl border border-stroke-primary bg-surface-primary p-1.5 shadow-lg"
      content={
        <div className="flex flex-col">
          <Typo
            element="span"
            type="labelXS"
            textColor="text-text-tertiary"
            className="px-3 pb-1 pt-2"
          >
            {translations.showColumns}
          </Typo>
          {funnelCols.map((c) => (
            <Row key={c.key} onClick={() => onToggle(c.key)}>
              <Typo element="span" type="bodySM" textColor="text-text-primary">
                {c.label}
              </Typo>
              <CheckIcon
                className={clsx(
                  "ml-auto h-4 w-4 shrink-0 text-icon-brand-default",
                  hidden[c.key] ? "opacity-0" : "opacity-100",
                )}
                aria-hidden="true"
                focusable="false"
              />
            </Row>
          ))}
          <div className="mx-2 my-1.5 h-px bg-stroke-primary" />
          <Typo
            element="span"
            type="labelXS"
            textColor="text-text-tertiary"
            className="px-3 pb-1 pt-2"
          >
            {translations.conversionRate}
          </Typo>
          {conversionCols.map((c) => (
            <Row key={c.key} onClick={() => onToggle(c.key)}>
              <span className="flex min-w-0 flex-col gap-1">
                <Typo
                  element="span"
                  type="bodySM"
                  textColor="text-text-primary"
                >
                  {c.label}
                </Typo>
                <Typo
                  element="span"
                  type="bodyXS"
                  textColor="text-text-tertiary"
                >
                  {c.menuLabel}
                </Typo>
              </span>
              <CheckIcon
                className={clsx(
                  "ml-auto h-4 w-4 shrink-0 text-icon-brand-default",
                  hidden[c.key] ? "opacity-0" : "opacity-100",
                )}
                aria-hidden="true"
                focusable="false"
              />
            </Row>
          ))}
        </div>
      }
    >
      <ButtonV2
        variant="tonal"
        size="md"
        // PopoverMenu toggles on every trigger click; row 85 tracks the
        // configurator opening only. aria-expanded still holds the pre-toggle
        // value here, so "false" means this click is an open.
        onClick={(event) => {
          if (event.currentTarget.getAttribute("aria-expanded") === "false") {
            analytics?.trackColumnsEvent?.();
          }
        }}
      >
        <span className="flex items-center gap-2">
          <GearIcon className="h-4 w-4" aria-hidden="true" focusable="false" />
          <Typo element="span" type="labelSM">
            {translations.columnsButton}
          </Typo>
        </span>
      </ButtonV2>
    </PopoverMenu>
  );
};
